import { inject, Injectable, signal } from '@angular/core';

import { NetworkService } from './network.service';
import { environment } from '../../../environments/environment';
import { V1TokenType } from '../interface/model/v1-token-type';
import { Result, ok, err, isErr } from '../utils/result';

export class CancelledError extends Error {
  constructor() {
    super('cancelled');
    this.name = 'CancelledError';
  }
}

// Extract a plain string from any thrown value.
// The WASM SDK rejects Promises with raw strings, not Error objects.
function toErrorMsg(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (typeof e === 'string') return e;
  return String(e);
}

function isWssError(e: unknown): boolean {
  const lower = toErrorMsg(e).toLowerCase();
  return (
    lower.includes('websocket') ||
    (lower.includes('connect failed') && !lower.includes('bt connect failed')) ||
    lower.includes('server closed connection') ||
    lower.includes('timeout') ||
    lower.includes('deadline exceeded')
  );
}

function isCancellationError(e: unknown): boolean {
  const msg = toErrorMsg(e);
  // SDK preserves JS error name as a prefix: "NotAllowedError: ..."
  if (msg.includes('NotAllowedError')) return true;
  // Chromium raises NotFoundError for user dismissal of the picker
  if (msg.includes('NotFoundError') && msg.toLowerCase().includes('cancel')) return true;
  const lower = msg.toLowerCase();
  return lower.includes('user cancel') || lower.includes('user denied');
}

export const BLE_NOT_SUPPORTED_MSG =
  'Bluetooth is not supported in this browser. Please open the app.';

// Map raw SDK/browser error strings to messages that are safe to show to users.
function toFriendlyMessage(e: unknown): string {
  const raw = toErrorMsg(e);
  const lower = raw.toLowerCase();
  if (
    lower.includes('web bluetooth is not supported') ||
    lower.includes('bluetooth is not supported') ||
    lower.includes('web bluetooth api globally disabled')
  ) {
    return BLE_NOT_SUPPORTED_MSG;
  }
  if (lower.includes('context deadline exceeded') || lower.includes('rpc timeout')) {
    return 'Timed out. Move closer to the lock.';
  }
  if (lower.includes('bt connect failed')) {
    return 'Bluetooth connection failed. Move closer to the lock.';
  }
  if (lower.includes('bt getservice failed') || lower.includes('bt get characteristic failed')) {
    return 'Lock not responding.';
  }
  if (lower.includes('startnotifications')) {
    return 'Failed to start lock notifications.';
  }
  if (lower.includes('write packet chunk failed')) {
    return 'Connection lost during unlock.';
  }
  if (
    lower.includes('websocket') ||
    lower.includes('connect failed') ||
    lower.includes('server closed connection')
  ) {
    return 'Server unreachable. Check your internet connection.';
  }
  if (lower.includes('rpc error')) {
    return 'Server error.';
  }
  return raw;
}

export interface UnlockParams {
  btId: string;
  apiKey?: string;
  wsURL?: string;
  guestKey?: string;
  version?: number;
}

export interface HecateSDKInstance {
  Unlock(params: UnlockParams): Promise<boolean>;
}

declare global {
  interface Window {
    HecateSDK?: (options: { mode: 'online' | 'offline' }) => Promise<HecateSDKInstance>;
  }
}

export type WasmStatus = 'uninitialized' | 'loading' | 'ready' | 'error';

/** Result of a successful unlock. `online` true means the backend emitted the
 * TOKEN_USED event itself, so the caller must NOT report it to /v1/used. */
export interface UnlockOutcome {
  message: string;
  online: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class WasmService {
  private readonly networkService = inject(NetworkService);

  private readonly _status = signal<WasmStatus>('uninitialized');
  readonly status = this._status.asReadonly();

  private readonly _progressMessage = signal<string>('');
  readonly progressMessage = this._progressMessage.asReadonly();

  private readonly _progressStep = signal<number>(0);
  readonly progressStep = this._progressStep.asReadonly();

  private readonly _progressTotal = signal<number>(0);
  readonly progressTotal = this._progressTotal.asReadonly();

  private sdkInstance: HecateSDKInstance | null = null;
  private initPromise: Promise<Result<HecateSDKInstance, Error>> | null = null;
  private currentMode: 'online' | 'offline' | null = null;
  private initVersion = 0;

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('hecate:progress', (e: Event) => {
        const customEvent = e as CustomEvent;
        if (customEvent.detail) {
          const { step, total, message } = customEvent.detail;
          this._progressStep.set(step || 0);
          this._progressTotal.set(total || 0);
          if (message) {
            this._progressMessage.set(message);
          }
        }
      });
    }
  }

  ensureInitialized(
    tokenType: V1TokenType = V1TokenType.TokenTypeOffline,
  ): Promise<Result<HecateSDKInstance, Error>> {
    const isOnline =
      tokenType === V1TokenType.TokenTypeOnline ||
      (tokenType === V1TokenType.TokenTypeHybrid && this.networkService.isOnline());
    const mode = isOnline ? 'online' : 'offline';

    // Clear stale cached instance when mode changes (e.g. HYBRID connectivity flip)
    if (this.currentMode !== null && this.currentMode !== mode) {
      this.sdkInstance = null;
      this.initPromise = null;
    }

    // If already initialized with the correct mode, return the instance
    if (this.sdkInstance && this.currentMode === mode) {
      return Promise.resolve(ok(this.sdkInstance));
    }

    // If an initialization is in progress for the same mode, return it
    if (this.initPromise && this.currentMode === mode) {
      return this.initPromise;
    }

    this._status.set('loading');
    this.currentMode = mode;
    const myVersion = ++this.initVersion;

    this.initPromise = (async () => {
      if (!window.HecateSDK) {
        this._status.set('error');
        this.initPromise = null;
        return err(new Error('HecateSDK was not found on window'));
      }

      try {
        const sdk = await window.HecateSDK({ mode });
        // Guard: a mode flip may have started a newer init before this resolved.
        // Only apply results if this is still the current init.
        if (this.initVersion === myVersion) {
          this.sdkInstance = sdk;
          this._status.set('ready');
        }
        return ok(sdk);
      } catch (e: unknown) {
        if (this.initVersion === myVersion) {
          this._status.set('error');
          this.initPromise = null;
          this.currentMode = null;
        }
        return err(new Error(`HecateSDK failed to initialize: ${toErrorMsg(e)}`));
      }
    })();

    return this.initPromise;
  }

  async handleUnlock(
    bluetooth_id: string,
    guest_key: string | undefined,
    tokenType: V1TokenType = V1TokenType.TokenTypeOffline,
    apiKey?: string,
  ): Promise<Result<UnlockOutcome, Error>> {
    const online = this.networkService.isOnline();
    const effectiveType =
      tokenType === V1TokenType.TokenTypeHybrid
        ? online
          ? V1TokenType.TokenTypeOnline
          : V1TokenType.TokenTypeOffline
        : tokenType;

    const initResult = await this.ensureInitialized(effectiveType);
    if (isErr(initResult)) return err(new Error(toFriendlyMessage(initResult.error)));

    const sdk = initResult.value;

    try {
      this._progressMessage.set('Starting unlock...');
      this._progressStep.set(0);
      this._progressTotal.set(0);

      const params: UnlockParams = {
        btId: bluetooth_id,
      };

      const useOnlinePath =
        tokenType === V1TokenType.TokenTypeOnline ||
        (tokenType === V1TokenType.TokenTypeHybrid && online);

      if (useOnlinePath) {
        if (!online) {
          return err(new Error('No internet connection.'));
        }
        params.apiKey = apiKey || 'API_KEY_LIVE';
        params.wsURL = `${environment.wsUrl}/`;
        params.version = 2;
      } else {
        if (!guest_key) {
          return err(new Error('Offline unlock requires a guest key.'));
        }
        params.guestKey = guest_key;
      }

      if (!navigator.bluetooth) {
        return err(new Error(BLE_NOT_SUPPORTED_MSG));
      }

      const success = await sdk.Unlock(params);

      if (success) {
        this._progressMessage.set('Unlocked successfully!');
        // online path → backend emits the TOKEN_USED event; offline → caller reports it.
        return ok({ message: 'Unlocked successfully!', online: useOnlinePath });
      } else {
        return err(new Error('Device rejected the unlock.'));
      }
    } catch (e: unknown) {
      if (isCancellationError(e)) {
        return err(new CancelledError());
      }
      if (tokenType === V1TokenType.TokenTypeHybrid && guest_key && isWssError(e)) {
        return this.offlineFallback(bluetooth_id, guest_key);
      }
      const raw = toErrorMsg(e);
      console.error('handleUnlock error:', raw);
      return err(new Error(toFriendlyMessage(e)));
    }
  }

  private async offlineFallback(
    bluetooth_id: string,
    guest_key: string,
  ): Promise<Result<UnlockOutcome, Error>> {
    this.sdkInstance = null;
    this.initPromise = null;
    this.currentMode = null;

    const initResult = await this.ensureInitialized(V1TokenType.TokenTypeOffline);
    if (isErr(initResult)) return err(new Error(toFriendlyMessage(initResult.error)));

    const sdk = initResult.value;
    try {
      this._progressMessage.set('Trying offline unlock...');
      const success = await sdk.Unlock({ btId: bluetooth_id, guestKey: guest_key });
      if (success) {
        this._progressMessage.set('Unlocked successfully!');
        return ok({ message: 'Unlocked successfully!', online: false });
      }
      return err(new Error('Device rejected the unlock.'));
    } catch (e: unknown) {
      if (isCancellationError(e)) {
        return err(new CancelledError());
      }
      console.error('handleUnlock offline fallback error:', toErrorMsg(e));
      return err(new Error(toFriendlyMessage(e)));
    }
  }
}
